// src/pages/Register.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Button from '../components/customButtons/Button';
import { useAuth } from '../context/AuthContext';
import PasswordStrengthIndicator from '../components/security/PasswordStrengthIndicator';

const Register = () => {
  const navigate = useNavigate();
  const { registerAgribusiness, isAuthenticated, loading } = useAuth();

  const [formData, setFormData] = useState({
    role: 'agribusiness',
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    business: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  // Redirect if authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  // Redirect after successful registration (only if no verification required)
  useEffect(() => {
    if (apiMessage.type === 'success' && !isSubmitting && !requiresVerification) {
      const timer = setTimeout(() => navigate('/login'), 2000);
      return () => clearTimeout(timer);
    }
  }, [apiMessage, isSubmitting, navigate, requiresVerification]);

  // Enhanced validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Valid email address required';
    if (email.length > 254) return 'Email too long';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (password.length > 128) return 'Password too long';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    return '';
  };

  const validateName = (name, fieldName) => {
    if (!name.trim()) return `${fieldName} is required`;
    if (name.length > 50) return `${fieldName} too long`;
    if (!/^[a-zA-Z\s\-']+$/.test(name)) return `${fieldName} contains invalid characters`;
    return '';
  };

  const validateBusiness = (business) => {
    if (!business.trim()) return 'Business name is required';
    if (business.length > 100) return 'Business name too long';
    if (/[<>]/.test(business)) return 'Business name contains invalid characters';
    return '';
  };

  // Debounced email validation
  useEffect(() => {
    if (formData.email && currentStep === 2) {
      const timer = setTimeout(() => {
        const emailError = validateEmail(formData.email);
        if (emailError && formData.email) {
          setErrors(prev => ({ ...prev, email: emailError }));
        } else if (errors.email) {
          setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.email;
            return newErrors;
          });
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [formData.email, currentStep, errors.email]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Sanitize input - remove potentially dangerous characters
    let sanitizedValue = value;
    if (name === 'firstName' || name === 'lastName' || name === 'business') {
      sanitizedValue = value.replace(/[<>]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};

    newErrors.firstName = validateName(formData.firstName, 'First name');
    newErrors.lastName = validateName(formData.lastName, 'Last name');
    newErrors.business = validateBusiness(formData.business);

    // Remove empty error messages
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};

    newErrors.email = validateEmail(formData.email);
    newErrors.password = validatePassword(formData.password);

    // Remove empty error messages
    Object.keys(newErrors).forEach(key => {
      if (!newErrors[key]) delete newErrors[key];
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      setErrors({});
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(1);
    setErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setIsSubmitting(true);
    setApiMessage({ type: '', text: '' });

    try {
      console.log('🚀 Registering user...');

      const result = await registerAgribusiness(formData);

      console.log('📨 Registration result:', result);

      if (result.success) {
        if (result.requiresVerification) {
          // Store email for verification page
          setRegisteredEmail(formData.email);
          setRequiresVerification(true);
          
          // Redirect to success page with email information
          navigate('/registration-success', {
            state: {
              email: formData.email,
              emailFailed: result.emailFailed || false
            },
            replace: true
          });
        } else {
          // Auto-verified account - redirect to login
          setApiMessage({
            type: 'success',
            text: result.message || 'Registration successful! Redirecting to login...'
          });

          // Reset form
          setFormData({
            role: 'agribusiness',
            email: '',
            password: '',
            firstName: '',
            lastName: '',
            business: ''
          });
          setErrors({});
          setCurrentStep(1);

          // Redirect to login after delay
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        }
      } else {
        throw new Error(result.error || 'Registration failed');
      }

    } catch (err) {
      console.error('❌ Registration error:', err);

      let errorMessage = err.message || 'An unexpected error occurred during registration.';

      // Enhanced error messages
      if (err.message.includes('network') || err.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (err.message.includes('timeout')) {
        errorMessage = 'Registration timeout. Please try again.';
      } else if (err.message.includes('verification')) {
        errorMessage = 'Please verify your email before logging in.';
      } else if (err.message.includes('already exists') || err.message.includes('already registered')) {
        errorMessage = 'An account with this email already exists. Please try logging in or use a different email.';
      }

      setApiMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-neutral flex items-center justify-center font-sans text-primary text-xl">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-4 font-body">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) return null;

  return (
    <div className="h-screen bg-neutral flex flex-col justify-center px-4 sm:px-6 lg:px-8 font-sans overflow-hidden">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-4 text-center text-3xl sm:text-2xl font-extrabold text-primary mb-6 sm:mb-8 font-heading">
          Create Your Account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600 max-w-sm mx-auto px-2 font-body">
          Join us to empower your agribusiness with smart tracking and insights.
        </p>
      </div>

      <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow rounded-2xl sm:px-12 border border-gray-50 animate-fade-in">

          {apiMessage.text && (
            <div className={`mb-4 text-center font-medium p-3 rounded-lg border font-body
    ${apiMessage.type === 'success'
                ? 'bg-green-50 text-green-700 border-green-100'
                : apiMessage.type === 'warning'
                  ? 'bg-yellow-50 text-yellow-700 border-yellow-100'
                  : 'bg-red-50 text-red-600 border-red-100'}`}>
              {apiMessage.text}

              {/* Show email verification info for both success and warning types */}
              {(apiMessage.type === 'success' || apiMessage.type === 'warning') && requiresVerification && registeredEmail && (
                <div className={`mt-3 p-3 rounded-lg border ${apiMessage.type === 'warning'
                  ? 'bg-orange-50 border-orange-200'
                  : 'bg-blue-50 border-blue-200'
                  }`}>
                  <div className="flex items-center justify-center">
                    <svg className={`w-5 h-5 mr-2 ${apiMessage.type === 'warning' ? 'text-orange-600' : 'text-blue-600'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                    </svg>
                    <span className={`font-semibold ${apiMessage.type === 'warning' ? 'text-orange-700' : 'text-blue-700'
                      }`}>
                      {apiMessage.type === 'warning' ? 'Email Notice' : 'Verification Email Sent!'}
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${apiMessage.type === 'warning' ? 'text-orange-600' : 'text-blue-600'
                    }`}>
                    {apiMessage.type === 'warning'
                      ? `We've created your account but encountered issues sending to ${registeredEmail}. You can try logging in or request verification later.`
                      : `We've sent a verification link to ${registeredEmail}. Please check your inbox.`
                    }
                  </p>
                  {apiMessage.type === 'success' && (
                    <div className="mt-2 text-xs text-blue-500">
                      💡 <strong>Tip:</strong> Check your spam folder if you don't see the email.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step Progress Indicator */}
          <div className="flex justify-center mb-6">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm font-body ${currentStep >= 1 ? 'bg-primary' : 'bg-gray-300'}`}>
              1
            </div>
            <div className="flex items-center mx-2 w-16">
              <div className={`h-1 flex-grow rounded-full ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-300'}`}></div>
            </div>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm font-body ${currentStep >= 2 ? 'bg-primary' : 'bg-gray-300'}`}>
              2
            </div>
          </div>

          {requiresVerification ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2 font-heading">Check Your Email!</h3>
              <p className="text-gray-600 mb-4 font-body">
                We've sent a verification link to <strong className="text-primary">{registeredEmail}</strong>
              </p>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
                <p className="text-blue-700 text-sm font-body">
                  <strong>Next Steps:</strong>
                </p>
                <ol className="text-blue-600 text-sm mt-2 space-y-1 text-left font-body">
                  <li>1. Check your email inbox</li>
                  <li>2. Click the verification link in the email</li>
                  <li>3. Return here to login with your verified account</li>
                </ol>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={() => {
                    setRequiresVerification(false);
                    setRegisteredEmail('');
                    setApiMessage({ type: '', text: '' });
                  }}
                  className="w-full"
                  variant="primary"
                >
                  Register Another Account
                </Button>
                <Link
                  to="/login"
                  className="block text-primary hover:text-primary-light font-medium text-sm hover:underline font-body"
                >
                  Already verified? Proceed to Login
                </Link>
              </div>
            </div>
          ) : (
            <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit} noValidate>
              {currentStep === 1 && (
                <div className="animate-fade-in">
                  <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4 mt-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                        First name *
                      </label>
                      <input
                        id="firstName"
                        name="firstName"
                        type="text"
                        required
                        maxLength="50"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        className={`block w-full border ${errors.firstName ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                                 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                      />
                      {errors.firstName && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.firstName}</p>}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                        Last name *
                      </label>
                      <input
                        id="lastName"
                        name="lastName"
                        type="text"
                        required
                        maxLength="50"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        className={`block w-full border ${errors.lastName ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                                 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                      />
                      {errors.lastName && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="mt-6">
                    <label htmlFor="business" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                      Company Name *
                    </label>
                    <input
                      id="business"
                      name="business"
                      type="text"
                      required
                      maxLength="100"
                      value={formData.business}
                      onChange={handleChange}
                      placeholder="AgriSolutions Inc."
                      className={`block w-full border ${errors.business ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                               focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                    />
                    {errors.business && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.business}</p>}
                  </div>

                  <div className="mt-8">
                    <Button
                      type="button"
                      onClick={handleNextStep}
                      className="w-full py-3 px-4 text-base rounded-lg font-accent"
                      variant="primary"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="animate-fade-in">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                      Email address *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      maxLength="254"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className={`block w-full border ${errors.email ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4
                               focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                    />
                    {errors.email && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.email}</p>}
                  </div>

                  <div className="mt-6">
                    <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1 font-body">
                      Password *
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        required
                        minLength="8"
                        maxLength="128"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                        className={`block w-full border ${errors.password ? 'border-red-400' : 'border-gray-300'} rounded-lg shadow-sm py-2.5 px-4 pr-10
                                 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary sm:text-base font-body`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(prev => !prev)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.54 18.54 0 0 1 2.58-3.15M2.37 2.37L21.63 21.63M16.13 16.13a4 4 0 0 1-5.42-5.42M4.72 4.72A10.07 10.07 0 0 1 12 4c7 0 11 8 11 8a18.54 18.54 0 0 1-2.58 3.15" />
                          </svg>
                        ) : (
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {errors.password && <p className="mt-1 text-sm text-red-600 font-medium font-body">{errors.password}</p>}

                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <PasswordStrengthIndicator password={formData.password} />
                    )}

                    <div className="mt-2 text-xs text-gray-500 font-body">
                      Password must be at least 8 characters with uppercase, lowercase, and number.
                    </div>
                  </div>

                  <div className="flex justify-between mt-8">
                    <Button
                      type="button"
                      onClick={handlePrevStep}
                      className="py-3 px-6 text-base rounded-lg font-accent"
                      variant="secondary"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="py-3 px-6 text-base rounded-lg font-accent"
                      variant="primary"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Registering...
                        </span>
                      ) : 'Register'}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          )}

          {!requiresVerification && (
            <div className="mt-6 sm:mt-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white text-gray-500 font-medium font-body">
                    Already have an account?
                  </span>
                </div>
              </div>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-primary hover:text-primary-light font-medium text-sm hover:underline font-body"
                >
                  Sign in to your account
                </Link>
              </div>
            </div>
          )}

          {/* Security Notice */}
          <div className="mt-6 text-xs text-gray-500 text-center font-body">
            <p>Your data is protected with industry-standard security measures.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;